import { logClientError, logServerSuccess } from '@utils/server-log';
import {
  FEATURE_RESOURCE_MAP,
  NAVIGABLE_FEATURE_KEYS,
  parsePermission,
} from '@models/permissions';

const LOG_PREFIX = '[Permissions]';

/** Handle global exposto no `window` para inspeção sob demanda (inclusive em produção). */
const DEBUG_HANDLE = '__permissions';

export interface PermissionsSnapshotSource {
  /** Permissões cruas do GetUserPermissions (`resource:action`). */
  permissions: string[];
  /** Resources com `:read`. */
  readableResources: string[];
  /** Retorno cru do GetUserMenu. */
  menuFeatures: string[] | undefined;
  /** Features efetivas usadas por guards, menu e home. */
  effectiveFeatures: string[];
  /** Decide a visibilidade de cada featureKey (mesma função usada pelo menu). */
  hasFeatureAccess: (featureKey: string) => boolean;
}

interface FeatureAccessRow {
  featureKey: string;
  resource: string;
  requires: string;
  granted: boolean;
  /** `menu-only` sinaliza item exibido no GetUserMenu sem a permissão exigida. */
  source: 'permission' | 'menu-only' | 'denied';
}

/** Permissão exigida por featureKey — espelha `PermissionsService.canAccessFeature`. */
function requiredPermissionFor(featureKey: string): { resource: string; requires: string } {
  const resource = FEATURE_RESOURCE_MAP[featureKey] ?? featureKey;
  return { resource, requires: `${resource}:read` };
}

/** Matriz featureKey -> permissão exigida -> concedida, com a origem da decisão. */
export function buildFeatureAccessMatrix(source: PermissionsSnapshotSource): FeatureAccessRow[] {
  return NAVIGABLE_FEATURE_KEYS.map((featureKey) => {
    const { resource, requires } = requiredPermissionFor(featureKey);
    const granted = source.hasFeatureAccess(featureKey);
    const inMenu = source.menuFeatures?.includes(featureKey) ?? false;

    return {
      featureKey,
      resource,
      requires,
      granted,
      source: granted ? 'permission' : inMenu ? 'menu-only' : 'denied',
    };
  });
}

/** Snapshot serializável — retornado por `window.__permissions()`. */
export function buildPermissionsSnapshot(source: PermissionsSnapshotSource) {
  return {
    permissions: [...source.permissions].sort(),
    readableResources: [...source.readableResources].sort(),
    menuFeatures: source.menuFeatures ?? null,
    effectiveFeatures: [...source.effectiveFeatures].sort(),
    featureAccess: buildFeatureAccessMatrix(source),
  };
}

/** Diagnóstico de permissões sujeito à política central de logs por ambiente. */
export function logPermissionsSnapshot(source: PermissionsSnapshotSource, context: string): void {
  const snapshot = buildPermissionsSnapshot(source);

  logServerSuccess('FRONTEND', `${LOG_PREFIX} ${context}`, snapshot);

  const menuOnly = snapshot.featureAccess.filter((row) => row.source === 'menu-only');
  if (menuOnly.length) {
    logClientError(
      'Módulos presentes no GetUserMenu mas BLOQUEADOS por falta de permissão:',
      menuOnly.map((row) => `${row.featureKey} (faltando ${row.requires})`)
    );
  }

}

/** Agrupa as permissões cruas por resource — útil para conferir ações finas. */
export function groupPermissionsByResource(permissions: string[]): Record<string, string[]> {
  return permissions.reduce<Record<string, string[]>>((acc, permission) => {
    const parsed = parsePermission(permission);
    if (!parsed) {
      return acc;
    }
    (acc[parsed.resource] ??= []).push(parsed.action);
    return acc;
  }, {});
}

/**
 * Publica `window.__permissions()` para inspeção manual no console — o log de
 * bootstrap acontece uma vez só e costuma ser perdido no reload em produção.
 */
export function exposePermissionsDebug(getSource: () => PermissionsSnapshotSource): void {
  if (typeof window === 'undefined') {
    return;
  }

  (window as unknown as Record<string, unknown>)[DEBUG_HANDLE] = () => {
    const source = getSource();
    const snapshot = buildPermissionsSnapshot(source);
    logServerSuccess('FRONTEND', `${LOG_PREFIX} inspeção manual`, {
      ...snapshot,
      byResource: groupPermissionsByResource(source.permissions),
    });
    return snapshot;
  };
}
